import { api } from './api';
import { decryptBlob, decryptJson, fromB64 } from '../../../core/crypto';

interface Entry {
  url: string;
  mimetype: string;
  filename: string;
  refcount: number;
  lastReleasedAt: number;
}

interface Pending {
  promise: Promise<{ url: string; mimetype: string; filename: string }>;
}

const MAX_IDLE_ENTRIES = 128;

const entries = new Map<string, Entry>();
const pending = new Map<string, Pending>();

async function fetchAndDecrypt(
  attachmentId: string,
  vaultKey: CryptoKey,
): Promise<{ url: string; mimetype: string; filename: string }> {
  const fetched = await api.getAttachment(attachmentId);
  const meta = await decryptJson<{ filename?: string; mimetype?: string }>(
    fetched.metadata,
    vaultKey,
  );
  const bytes = await decryptBlob(fromB64(fetched.iv), fetched.blob, vaultKey);
  const filename = meta.filename || `attachment-${attachmentId}`;
  const mimetype = meta.mimetype || 'application/octet-stream';
  const blob = new Blob([bytes as BlobPart], { type: mimetype });
  const url = URL.createObjectURL(blob);
  return { url, mimetype, filename };
}

function evictIfOverCap(): void {
  const idle: Array<{ id: string; at: number }> = [];
  for (const [id, e] of entries) {
    if (e.refcount <= 0) idle.push({ id, at: e.lastReleasedAt });
  }
  if (idle.length <= MAX_IDLE_ENTRIES) return;
  idle.sort((a, b) => a.at - b.at);
  const toEvict = idle.slice(0, idle.length - MAX_IDLE_ENTRIES);
  for (const { id } of toEvict) {
    const e = entries.get(id);
    if (!e) continue;
    URL.revokeObjectURL(e.url);
    entries.delete(id);
  }
}

export async function acquireAttachmentUrl(
  attachmentId: string,
  vaultKey: CryptoKey,
): Promise<{ url: string; mimetype: string; filename: string }> {
  const existing = entries.get(attachmentId);
  if (existing) {
    existing.refcount += 1;
    return { url: existing.url, mimetype: existing.mimetype, filename: existing.filename };
  }
  const inFlight = pending.get(attachmentId);
  if (inFlight) {
    const r = await inFlight.promise;
    // A concurrent caller resolved; bump the refcount that the shared
    // decrypt created for this acquirer.
    const e = entries.get(attachmentId);
    if (e) e.refcount += 1;
    return r;
  }
  const promise = (async () => {
    const r = await fetchAndDecrypt(attachmentId, vaultKey);
    entries.set(attachmentId, {
      url: r.url,
      mimetype: r.mimetype,
      filename: r.filename,
      refcount: 1,
      lastReleasedAt: 0,
    });
    return r;
  })();
  pending.set(attachmentId, { promise });
  try {
    return await promise;
  } finally {
    pending.delete(attachmentId);
  }
}

export function releaseAttachmentUrl(attachmentId: string): void {
  const e = entries.get(attachmentId);
  if (!e) return;
  e.refcount -= 1;
  if (e.refcount <= 0) {
    e.refcount = 0;
    e.lastReleasedAt = Date.now();
    evictIfOverCap();
  }
}

export function purgeAttachmentBlobCache(): void {
  for (const [, e] of entries) {
    URL.revokeObjectURL(e.url);
  }
  entries.clear();
  pending.clear();
}
